import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PERSON_STATUSES, type PersonDetailWire } from "@releve/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUserOrgRole } from "@/hooks/useCurrentUserOrgRole";
import { useInsertConsentRecord } from "@/hooks/useGdprConsent";
import { useOrgNavigate } from "@/hooks/useOrgNavigate";
import { useCreatePerson, useUpdatePerson } from "@/hooks/usePeople";
import { useLinkedPersonBusiness, useUpsertPersonBusiness } from "@/hooks/usePersonBusiness";
import { useUpsertPersonDEI } from "@/hooks/usePersonDEI";
import { useTranslation } from "@/hooks/useTranslation";
import { toast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { LabelWithError } from "./fields";
import { PersonDangerZone } from "./PersonDangerZone";
import { PersonDeiSection } from "./PersonDeiSection";
import { PersonDetailsSection } from "./PersonDetailsSection";
import { PersonDisciplinesSkillsSection } from "./PersonDisciplinesSkillsSection";
import { PersonEmergencyContactsSection } from "./PersonEmergencyContactsSection";
import { PersonTypeToggleGroup } from "./PersonTypeToggleGroup";
import {
  EMPTY_BUSINESS_IDENTITY,
  UNTOUCHED,
  buildPersonPayload,
  businessIdentitySchema,
  childDraftsFromPerson,
  createPersonFormSchema,
  deiFormDefaults,
  deiFormSchema,
  personFormDefaults,
  validateEmergencyContacts,
  type BusinessIdentityFormValues,
  type DeiFormValues,
  type EmergencyContactErrors,
  type PersonChildDrafts,
  type PersonChildTouched,
  type PersonFormValues,
} from "./personFormSchema";

/** The form's sections, top to bottom. `defaultTab` names one to scroll to. */
export type PersonFormSection = "basic" | "disciplines" | "dei" | "emergency";

/**
 * Old deep-link values: the Contact / Professional / Business tabs are now
 * part of the Details section, and connections moved to the detail page's
 * Connections tab.
 */
const LEGACY_DETAIL_TABS = new Set(["contact", "professional", "business", "connections"]);
const normalizeSection = (tab: string | undefined): PersonFormSection =>
  !tab || LEGACY_DETAIL_TABS.has(tab) ? "basic" : (tab as PersonFormSection);

export interface PersonFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The person to edit (the full detail). Omit to create. */
  person?: PersonDetailWire | null;
  /**
   * Edit mode while the detail is still loading (`PersonEditHost`): the
   * dialog opens at once with a skeleton body and Save disabled.
   */
  personLoading?: boolean;
  /** After a successful create / update (and its follow-up requests). */
  onSuccess?: (person: PersonDetailWire) => void;
  /** After a delete from the danger zone. The caller decides whether to navigate. */
  onDeleted?: () => void;
  /** Person types pre-selected on create. */
  defaultPersonTypes?: string[];
  /**
   * The section to scroll to on open (`basic` | `disciplines` | `dei` |
   * `emergency`). The name predates the single-page layout, when these were tabs.
   */
  defaultTab?: string;
  /** On create, continue to the new person's page (default true). */
  navigateOnCreate?: boolean;
}

/**
 * The person create / edit form — a `FullscreenFormDialog` in the document
 * metaphor (docs/FULLSCREEN_FORM_MODALS.md): the classification row (types,
 * status) above the document card, the hero, then every section on one page
 * — Details, Disciplines & skills, DEI (admins only), Emergency contacts.
 *
 * Save is ONE atomic request for the person and its four child sets
 * (`create_person` / `update_person`); DEI (admins only) and the business
 * identity are follow-up requests — when one fails the person is still saved
 * and a specific warning says which part was not.
 *
 * DEI is rendered, read and written only for an org ADMIN (the org role —
 * the super-admin bypass does not count); for everyone else there is no DEI
 * section and no DEI request.
 */
export function PersonFormDialog({
  open,
  onOpenChange,
  person,
  personLoading = false,
  onSuccess,
  onDeleted,
  defaultPersonTypes,
  defaultTab = "basic",
  navigateOnCreate = true,
}: PersonFormDialogProps) {
  const { t } = useTranslation("people");
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;
  const orgNavigate = useOrgNavigate();
  const { user } = useAuth();
  const { isAdmin, isLoading: roleLoading, can } = useCurrentUserOrgRole();
  // Vendor bank identity is accounting data (org roles step 3): hidden from a
  // role without `accounting`, and never sent for it.
  // While the role is still loading can() reads false: the form must not
  // submit then, or an accounting role's save would send a stripped identity
  // and the server (which knows the role) would CLEAR the stored IBAN / VAT.
  const showVendorIdentity = !roleLoading && can("accounting");
  const showDei = isAdmin && !roleLoading;
  const isEdit = !!person || personLoading;
  // A GDPR-erased person is refused by update_person (RV002): lock the form
  // up front instead of letting the server say no after the user typed.
  const erased = !!person?.archive_only_reasons.erased;
  const locked = personLoading || erased;

  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson();
  const upsertPersonBusiness = useUpsertPersonBusiness();
  const upsertPersonDEI = useUpsertPersonDEI();
  const insertConsentRecord = useInsertConsentRecord();
  const { data: linkedBusiness, isLoading: linkedBusinessLoading } = useLinkedPersonBusiness(
    person?.linked_business_id,
  );

  const personSchema = useMemo(() => createPersonFormSchema(t), [t]);
  const form = useForm<PersonFormValues>({
    resolver: zodResolver(personSchema),
    defaultValues: personFormDefaults(person, defaultPersonTypes),
  });
  const businessForm = useForm<BusinessIdentityFormValues>({
    resolver: zodResolver(businessIdentitySchema),
    defaultValues: EMPTY_BUSINESS_IDENTITY,
  });
  const deiForm = useForm<DeiFormValues>({
    resolver: zodResolver(deiFormSchema),
    defaultValues: deiFormDefaults(showDei ? person?.dei : null),
  });

  const [drafts, setDrafts] = useState<PersonChildDrafts>(() => childDraftsFromPerson(person));
  const [touched, setTouched] = useState<PersonChildTouched>(UNTOUCHED);
  const [contactErrors, setContactErrors] = useState<EmergencyContactErrors>({});
  const sectionRefs = useRef<Partial<Record<PersonFormSection, HTMLDivElement | null>>>({});
  // The DEI collapsible starts open when the person already has DEI answers
  // (consent on file) or a caller deep-links to it; otherwise it stays folded.
  const deiInitiallyOpen = () => normalizeSection(defaultTab) === "dei" || !!person?.dei?.dei_data_consent;
  const [deiOpen, setDeiOpen] = useState(deiInitiallyOpen);
  const [submitting, setSubmitting] = useState(false);

  // Reset everything when the dialog opens or the edited record changes.
  // Keyed on open + person?.id — NOT the person object — so a refetch of the
  // same person can never clobber in-progress edits.
  useEffect(() => {
    if (!open) return;
    form.reset(personFormDefaults(person, defaultPersonTypes));
    deiForm.reset(deiFormDefaults(showDei ? person?.dei : null));
    setDrafts(childDraftsFromPerson(person));
    setTouched(UNTOUCHED);
    setContactErrors({});
    setDeiOpen(deiInitiallyOpen());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- id-keyed reset (see above).
  }, [open, person?.id]);

  // The org role can resolve after the dialog opened: hydrate DEI then,
  // unless the admin has already started editing it.
  useEffect(() => {
    if (!open || !showDei || deiForm.formState.isDirty) return;
    deiForm.reset(deiFormDefaults(person?.dei));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once the role / person land.
  }, [open, showDei, person?.id]);

  // Hydrate the business identity once the linked business loads, unless the
  // user has already edited it.
  useEffect(() => {
    if (!open || businessForm.formState.isDirty) return;
    const identity = linkedBusiness?.identity;
    businessForm.reset({
      business_name: linkedBusiness?.name ?? "",
      trading_name: identity?.trading_name ?? "",
      country_code: identity?.country_code ?? "",
      legal_form_id: identity?.legal_form_id ?? null,
      registration_number: identity?.registration_number ?? "",
      vat_number: identity?.vat_number ?? "",
      iban: identity?.iban ?? "",
      bic: identity?.bic ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the linked business id.
  }, [open, person?.id, linkedBusiness?.id]);

  const scrollToSection = (section: PersonFormSection) => {
    // After paint, so a section mounted this render (the form body replaces
    // the loading skeleton) has a box to scroll to.
    requestAnimationFrame(() =>
      // Optional call: jsdom (the component tests) has no scrollIntoView.
      sectionRefs.current[section]?.scrollIntoView?.({ behavior: "smooth", block: "start" }),
    );
  };

  // Open on the requested section (the detail page's "edit disciplines"
  // shortcut), and follow a caller re-targeting an already-open dialog.
  // Details is the top of the document, so it needs no scroll.
  const targetSection = normalizeSection(defaultTab);
  useEffect(() => {
    if (!open || personLoading || targetSection === "basic") return;
    if (targetSection === "dei") {
      if (!showDei) return;
      setDeiOpen(true);
    }
    scrollToSection(targetSection);
  }, [open, personLoading, targetSection, showDei]);

  const setChild = <K extends keyof PersonChildDrafts>(key: K) => (next: PersonChildDrafts[K]) => {
    setDrafts((prev) => ({ ...prev, [key]: next }));
    setTouched((prev) => ({ ...prev, [key]: true }));
    if (key === "emergency_contacts") setContactErrors({});
  };

  /** DEI follow-up: upsert with consent, clear on withdrawal, record the consent event. */
  const saveDei = async (saved: PersonDetailWire, warnings: string[]) => {
    if (!organizationId) return;
    const values = deiForm.getValues();
    const existing = person?.dei ?? null;
    const hadConsent = !!existing?.dei_data_consent;
    if (!values.dei_data_consent && !hadConsent) return;

    try {
      if (values.dei_data_consent) {
        await upsertPersonDEI.mutateAsync({
          person_id: saved.id,
          organization_id: organizationId,
          gender: values.gender,
          pronouns: values.pronouns,
          ethnicity: values.ethnicity,
          race: values.race,
          sexual_orientation: values.sexual_orientation,
          disability_status: values.disability_status,
          veteran_status: values.veteran_status,
          age_range: values.age_range,
          primary_language: values.primary_language,
          religious_affiliation: values.religious_affiliation,
          socioeconomic_background: values.socioeconomic_background,
          dei_survey_completed_at: existing?.dei_survey_completed_at ?? null,
          dei_data_consent: true,
        });
      } else {
        // Withdrawn: every answer is cleared (the `valid_consent` CHECK).
        await upsertPersonDEI.mutateAsync({
          person_id: saved.id,
          organization_id: organizationId,
          dei_survey_completed_at: existing?.dei_survey_completed_at ?? null,
          dei_data_consent: false,
        });
      }
    } catch {
      warnings.push(t("form.toast.deiNotSaved"));
      return;
    }

    if (values.dei_data_consent === hadConsent) return;
    try {
      await insertConsentRecord.mutateAsync({
        organization_id: organizationId,
        person_id: saved.id,
        consent_type: "dei_data",
        consent_version: "1.0",
        consent_text: values.dei_data_consent ? t("form.dei.consentRecordGranted") : t("form.dei.consentRecordWithdrawn"),
        event: values.dei_data_consent ? "granted" : "withdrawn",
        ...(values.dei_data_consent ? {} : { withdrawn_at: new Date().toISOString() }),
        collection_method: "in_app",
        collected_by: user?.id ?? null,
      });
    } catch {
      warnings.push(t("form.toast.consentNotRecorded"));
    }
  };

  /** Business-identity follow-up — the person's own `businesses` row. */
  const saveBusiness = async (saved: PersonDetailWire, values: PersonFormValues, warnings: string[]) => {
    const b = businessForm.getValues();
    const name =
      b.business_name.trim() ||
      values.display_name.trim() ||
      `${values.first_name} ${values.last_name}`.trim() ||
      values.company.trim();
    try {
      await upsertPersonBusiness.mutateAsync({
        personId: saved.id,
        identity: !showVendorIdentity ? { name } : {
          name,
          trading_name: b.trading_name || null,
          country_code: b.country_code || null,
          legal_form_id: b.legal_form_id || null,
          registration_number: b.registration_number || null,
          vat_number: b.vat_number || null,
          iban: b.iban || null,
          bic: b.bic || null,
        },
      });
    } catch {
      warnings.push(t("form.toast.businessNotSaved"));
    }
  };

  const handleSubmit = async (values: PersonFormValues) => {
    if (!organizationId || locked || roleLoading) return;
    const errors = validateEmergencyContacts(drafts.emergency_contacts, t);
    setContactErrors(errors);
    if (Object.keys(errors).length > 0) {
      scrollToSection("emergency");
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPersonPayload(values, drafts, person ? touched : "all");
      let saved: PersonDetailWire;
      try {
        saved = person
          ? await updatePerson.mutateAsync({ id: person.id, ...payload })
          : await createPerson.mutateAsync(payload);
      } catch {
        // The person hooks toast the failure and the MutationCache reports it;
        // the dialog stays open with everything the user typed.
        return;
      }

      const warnings: string[] = [];
      if (showDei && deiForm.formState.isDirty) await saveDei(saved, warnings);
      // Only write the business identity when something about it changed (or
      // there is no linked business yet) — an unrelated edit leaves it alone.
      const businessNeedsSave =
        businessForm.formState.isDirty ||
        !person?.linked_business_id ||
        form.getFieldState("is_business").isDirty;
      if (values.is_business && businessNeedsSave) await saveBusiness(saved, values, warnings);
      warnings.forEach((warning) => toast.warning(warning));

      // A create continues to the new person's page. navigate(replace) runs
      // BEFORE the close, so FullscreenFormDialog sees its history entry gone
      // and skips its own history.back().
      if (!person && navigateOnCreate && saved.code) {
        orgNavigate(`/resources/people/${saved.code}`, { replace: true });
      }
      onOpenChange(false);
      onSuccess?.(saved);
    } finally {
      setSubmitting(false);
    }
  };

  // Alternating tinted bands below Details, so a long single-page form still
  // reads as separate blocks — the sanctioned multi-band deviation in
  // docs/FULLSCREEN_FORM_MODALS.md. Details itself ends on a band (Business),
  // so these continue plain → band: Disciplines plain, Emergency banded. A band
  // that ends the card bleeds into its bottom edge (the
  // CreateOrUpdateAccountModal treatment).
  // DEI lives below the card, so only these two alternate inside it.
  const stripedSections: PersonFormSection[] = ["disciplines", "emergency"];
  const sectionClass = (section: PersonFormSection) => {
    const index = stripedSections.indexOf(section);
    const banded = index % 2 === 1;
    const last = index === stripedSections.length - 1;
    return cn(
      "scroll-mt-6",
      banded && "-mx-6 px-6 py-6 bg-muted",
      banded && last && "-mb-6 rounded-b-lg",
    );
  };

  const anyTouched = Object.values(touched).some(Boolean);
  const pristine =
    !form.formState.isDirty && !businessForm.formState.isDirty && !deiForm.formState.isDirty && !anyTouched;

  return (
    <FullscreenFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t("form.editTitle") : t("form.createTitle")}
      description={isEdit ? t("form.editDescription") : t("form.createDescription")}
      onSubmit={form.handleSubmit(handleSubmit)}
      submitLabel={isEdit ? t("form.updateButton") : t("form.createButton")}
      submittingLabel={isEdit ? t("form.updating") : t("form.creating")}
      isSubmitting={submitting}
      skipCloseConfirmation={pristine}
      submitDisabled={locked || roleLoading || (!!person?.linked_business_id && linkedBusinessLoading)}
      formId="person-form"
    >
      <Form {...form}>
        <div className="space-y-8">
          {/* Classification row — above the document card. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="person_type"
              render={({ field }) => (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("form.fields.personTypes")}
                  </Label>
                  <PersonTypeToggleGroup
                    value={field.value}
                    onChange={field.onChange}
                    ariaLabel={t("form.fields.personTypes")}
                    disabled={locked}
                  />
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field, fieldState }) => (
                <FormItem>
                  <LabelWithError label={t("form.fields.status")} error={fieldState.error?.message} />
                  <Select value={field.value} onValueChange={field.onChange} disabled={locked}>
                    <FormControl>
                      <SelectTrigger className={cn(fieldState.error && "border-destructive")}>
                        <SelectValue placeholder={t("form.placeholders.status")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PERSON_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {t(`common:people.status.${status}`)}
                        </SelectItem>
                      ))}
                      {field.value && !(PERSON_STATUSES as readonly string[]).includes(field.value) && (
                        <SelectItem value={field.value}>{field.value}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          {/* The document */}
          <Card>
            <CardContent className="pt-6 space-y-8">
              <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                {t("form.heroTitle")}
              </h2>

              {erased && (
                <p role="note" className="text-sm text-muted-foreground" data-testid="person-form-erased">
                  {t("dangerZone.erased")}
                </p>
              )}

              {personLoading ? (
                <div className="space-y-4" data-testid="person-form-loading">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : (
                // One document, every section stacked. A single fieldset locks the
                // whole form for a GDPR-erased person.
                <fieldset disabled={erased} className="m-0 min-w-0 border-0 p-0 space-y-8">
                  <div ref={(el) => { sectionRefs.current.basic = el; }} className="scroll-mt-6">
                    <PersonDetailsSection
                      form={form}
                      businessForm={businessForm}
                      locations={drafts.locations}
                      onLocationsChange={setChild("locations")}
                      hasLinkedBusiness={!!person?.linked_business_id}
                      showVendorIdentity={showVendorIdentity}
                    />
                  </div>

                  <div ref={(el) => { sectionRefs.current.disciplines = el; }} className={sectionClass("disciplines")}>
                    <PersonDisciplinesSkillsSection
                      organizationId={organizationId}
                      disciplines={drafts.disciplines}
                      onDisciplinesChange={setChild("disciplines")}
                      skills={drafts.skills}
                      onSkillsChange={setChild("skills")}
                    />
                  </div>

                  <div ref={(el) => { sectionRefs.current.emergency = el; }} className={sectionClass("emergency")}>
                    <PersonEmergencyContactsSection
                      value={drafts.emergency_contacts}
                      onChange={setChild("emergency_contacts")}
                      errors={contactErrors}
                    />
                  </div>
                </fieldset>
              )}
            </CardContent>
          </Card>

          {/* DEI — below the document card, as a collapsible opt-in block
              (the "Advanced VAT options" / Recurring shell). Admins only. */}
          {showDei && !personLoading && (
            <div ref={(el) => { sectionRefs.current.dei = el; }} className="scroll-mt-6">
              <Form {...deiForm}>
                <fieldset disabled={erased} className="m-0 min-w-0 border-0 p-0">
                  <PersonDeiSection form={deiForm} open={deiOpen} onOpenChange={setDeiOpen} />
                </fieldset>
              </Form>
            </div>
          )}

          {/* Danger zone — edit mode only, after the document. */}
          {person && (
            <PersonDangerZone
              person={person}
              disabled={submitting}
              onArchived={(archived) => {
                onOpenChange(false);
                onSuccess?.(archived);
              }}
              onDeleted={() => {
                onDeleted?.();
                onOpenChange(false);
              }}
            />
          )}
        </div>
      </Form>
    </FullscreenFormDialog>
  );
}
